import { LinkClickedEvent, TabClickedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { FluidContainer } from '@atlassian/mpac-primitives';
import { useEnvironment, useSource } from '@atlassian/mpac-utils';
import { AppsWithImageHighlights, HeadingWithTabs } from '@atlassian/mpac-widgets';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';

import {
  ANALYTICS_SUBJECT_ID_APPS_WITH_IMAGE_HIGHLIGHTS_LINK,
  ANALYTICS_SUBJECT_ID_HEADING_WITH_TABS_TAB,
} from '../../../../../analytics/Events';
import useCTAClickHandler from '../../../../../hooks/useCTAClickHandler';
import { rovoCollectionConfig } from '../../config/rovoCollection.config';
import { CollectionsStructuredData } from '../StructuredData';
import { useCollectionsData } from './hooks/useCollectionsData';

import type { AppsWithImageHighlightsContent } from '@atlassian/mpac-widgets';

const allCategoryIds = rovoCollectionConfig.categories.map((cat) => cat.id);

export const RovoAppsCollections = () => {
  const source = useSource();
  const environment = useEnvironment();
  const [activeCategoryId, setActiveCategoryId] = useState<string>(
    rovoCollectionConfig.categories[0].id
  );

  const { ref: containerRef, inView: isInView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { apps, loading } = useCollectionsData({
    collectionId: rovoCollectionConfig.id,
    categoryId: activeCategoryId,
    allCategoryIds,
    shouldPrefetchCategories: isInView,
  });
  const fireEvent = useAnalyticsEvents();
  const handleLinkClick = useCTAClickHandler();

  const handleTabChange = (tabId: string) => {
    setActiveCategoryId(tabId);
    fireEvent({
      eventType: TabClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HEADING_WITH_TABS_TAB,
      attributes: {
        tabId,
        sectionName: rovoCollectionConfig.id,
      },
    });
  };

  const activeCategory = rovoCollectionConfig.categories.find(
    (category) => category.id === activeCategoryId
  );

  return (
    <div ref={containerRef}>
      <CollectionsStructuredData apps={apps || []} />
      <FluidContainer>
        <HeadingWithTabs
          title={rovoCollectionConfig.title}
          tabItems={rovoCollectionConfig.categories}
          activeTabId={activeCategoryId}
          onTabChange={handleTabChange}
        />
      </FluidContainer>

      <AppsWithImageHighlights
        apps={apps || []}
        loading={loading}
        environment={environment}
        source={source}
        content={activeCategory as AppsWithImageHighlightsContent}
        onLinkClick={(event) =>
          handleLinkClick(
            event,
            LinkClickedEvent,
            ANALYTICS_SUBJECT_ID_APPS_WITH_IMAGE_HIGHLIGHTS_LINK,
            {
              destinationUrl: activeCategory?.linkHref,
              linkText: activeCategory?.linkText,
            },
            activeCategory?.linkHref
          )
        }
      />
    </div>
  );
};
